import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Hammer, Trees, Filter } from "lucide-react";
import BeforeAfter from "../components/BeforeAfter";

// Toutes les réalisations classées par pôle (source unique de vérité).
const REALISATIONS = [
  // Propreté & Hygiène
  { pole: "proprete", key: "parquet", title: "Rénovation de parquet", caption: "Traitement anti-humidité, décapage et vitrification." },
  { pole: "proprete", key: "showroom", title: "Nettoyage fin de chantier", caption: "Évacuation, dépoussiérage et lustrage avant livraison." },
  { pole: "proprete", key: "terrasse-bois", title: "Terrasse extérieure", caption: "Nettoyage haute pression et ravivage des lames." },
  // Travaux & Rénovation
  { pole: "travaux", key: "renov-haussmann", title: "Rénovation appartement", caption: "Ravalement des moulures d'origine, ponçage et pose de parquet chêne, remise en peinture complète." },
  { pole: "travaux", key: "renov-gros-oeuvre", title: "Réaménagement gros œuvre", caption: "Abattage de cloisons, reprise des plâtreries, réfection électrique et pose de sols stratifiés." },
  { pole: "travaux", key: "renov-sdb", title: "Rénovation salle de bain", caption: "Dépose de la baignoire vétuste, faïence grand format, robinetterie encastrée et éclairage LED." },
  { pole: "travaux", key: "renov-sdb-zen", title: "Salle de bain contemporaine", caption: "Reprise complète du chantier, carrelage grand format, douche à l'italienne, vasque suspendue." },
  // Espaces Verts
  { pole: "verts", key: "ev-parterres", title: "Création de parterres", caption: "Nettoyage, désherbage, plantation raisonnée et paillage minéral." },
  { pole: "verts", key: "ev-tonte", title: "Tonte et remise en état", caption: "Débroussaillage, tonte fine et finition en bordures nettes." },
  { pole: "verts", key: "ev-sous-bois", title: "Entretien de sous-bois", caption: "Élagage sélectif, nettoyage du sol et remise en valeur des essences existantes." },
];

const FILTERS = [
  { id: "all", label: "Toutes", icon: Filter },
  { id: "proprete", label: "Propreté & Hygiène", icon: Sparkles },
  { id: "travaux", label: "Travaux & Rénovation", icon: Hammer },
  { id: "verts", label: "Espaces Verts", icon: Trees },
];

export default function Realisations() {
  const [active, setActive] = useState("all");
  const items = active === "all" ? REALISATIONS : REALISATIONS.filter((r) => r.pole === active);

  return (
    <div data-testid="realisations-page">
      {/* HERO */}
      <section
        className="bg-ink text-white relative overflow-hidden nx-hero-realisations"
        style={{
          paddingTop: 200,
          paddingBottom: 120,
          "--nx-hero-realisations-img": "url(/photos/before-after/renov-haussmann-apres.jpg)",
        }}
      >
        <div className="absolute inset-0 nx-hero-grid opacity-50"></div>
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20" style={{ background: "radial-gradient(circle, #00C8FF 0%, transparent 70%)" }}></div>
        <div className="nx-container relative">
          <div className="max-w-3xl nx-fade-up">
            <span className="nx-overline">Nos réalisations</span>
            <h1 className="text-white" style={{ marginTop: 24, marginBottom: 32 }}>
              Des engagements visibles. <span style={{ color: "#00C8FF" }}>Des résultats concrets.</span>
            </h1>
            <p className="text-mist" style={{ fontSize: 20, marginBottom: 0 }}>
              Chez NOVANEXT, nous préférons les preuves aux promesses. Chaque réalisation est documentée pour vous permettre de constater, en toute transparence, la qualité du travail accompli.<br />
              <strong style={{ color: "#FFFFFF" }}>Faites glisser. Comparez. Constatez.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* FILTRES + GALERIE */}
      <section className="bg-white nx-arch-grid-light" style={{ paddingTop: 80, paddingBottom: 120 }}>
        <div className="nx-container">
          {/* Filtres */}
          <div className="flex flex-wrap justify-center gap-3" data-testid="realisations-filters" style={{ marginBottom: 64 }}>
            {FILTERS.map((f) => {
              const Ic = f.icon;
              const isActive = active === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setActive(f.id)}
                  data-testid={`filter-${f.id}`}
                  className="inline-flex items-center gap-2"
                  style={{
                    padding: "10px 20px",
                    borderRadius: 999,
                    fontFamily: "Exo 2, sans-serif",
                    fontWeight: 600,
                    fontSize: 14,
                    letterSpacing: "0.02em",
                    background: isActive ? "#0D2B45" : "transparent",
                    color: isActive ? "#00C8FF" : "#0D2B45",
                    border: isActive ? "1.5px solid #00C8FF" : "1.5px solid rgba(13,43,69,0.15)",
                    cursor: "pointer",
                    transition: "all 0.22s cubic-bezier(0.4,0,0.2,1)",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.borderColor = "#00C8FF";
                      e.currentTarget.style.color = "#0099CC";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.borderColor = "rgba(13,43,69,0.15)";
                      e.currentTarget.style.color = "#0D2B45";
                    }
                  }}
                >
                  <Ic size={16} strokeWidth={1.75} />
                  {f.label}
                </button>
              );
            })}
          </div>

          {/* Résultats */}
          <p style={{ textAlign: "center", color: "#64748B", fontSize: 14, marginBottom: 40, letterSpacing: "0.06em" }} data-testid="realisations-count">
            {items.length} réalisation{items.length > 1 ? "s" : ""} affichée{items.length > 1 ? "s" : ""}
          </p>

          {/* Grille — 2 colonnes desktop, 1 mobile ; toutes les photos au même format 3:2 */}
          <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 40 }} data-testid="realisations-grid">
            {items.map((item, i) => (
              <div key={item.key} data-testid={`realisation-${item.key}`}>
                <BeforeAfter
                  beforeSrc={`/photos/before-after/${item.key}-avant.jpg`}
                  afterSrc={`/photos/before-after/${item.key}-apres.jpg`}
                  alt={item.title}
                  testId={`realisation-slider-${item.key}`}
                />
                <div style={{ marginTop: 20 }}>
                  <div style={{ fontSize: 12, color: "#00C8FF", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 6 }}>
                    {FILTERS.find((f) => f.id === item.pole)?.label}
                  </div>
                  <h4 style={{ fontFamily: "Exo 2, sans-serif", fontWeight: 700, fontSize: 22, color: "#0D2B45", marginBottom: 8 }}>{item.title}</h4>
                  <p style={{ fontSize: 15, color: "#475569", marginBottom: 0, lineHeight: 1.5 }}>{item.caption}</p>
                </div>
              </div>
            ))}
          </div>

          {items.length === 0 && (
            <div style={{ textAlign: "center", color: "#94A3B8", padding: 60 }}>Aucune réalisation dans cette catégorie pour l'instant.</div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy text-white nx-cta-services-bg" style={{ paddingTop: 120, paddingBottom: 120 }}>
        <div className="nx-container text-center">
          <span className="nx-overline">Votre chantier maintenant</span>
          <h2 className="text-white" style={{ marginTop: 24, marginBottom: 32, maxWidth: 860, marginLeft: "auto", marginRight: "auto" }}>
            Envie du même niveau de finition sur vos sites&nbsp;? Parlons-en.
          </h2>
          <Link to="/contact" className="nx-btn nx-btn-primary" data-testid="realisations-cta">
            Demander un devis <ArrowRight size={18} strokeWidth={2} />
          </Link>
        </div>
      </section>
    </div>
  );
}
